import React from "react";
import { Typography } from "@mui/material";
import Flex from "components/Flex";
import LoadingSpinner from "components/LoadingSpinner";
import { Skeleton } from "@mui/material";
import HitCard from "../HitCard";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { LensHit, MatchingBlock, processCardItem } from "Backend";
import { DocumentIntake } from "generated/graphql";
import { StateHandle } from "StateHandle";
import { styled } from "@mui/system";

const HitCardBox = styled(Flex)({
  marginTop: "1rem",
  marginBottom: "1rem",
  display: "flex",
});

export interface HitCardVirtuosoProps {
  lensId: number;
  caseId: number;
  intake: DocumentIntake | undefined;
  items: Array<LensHit | MatchingBlock>;
  itemsFullCount: number;
  loading: boolean;
  numShowingHandle: StateHandle<number>;
  firstShowingHandle: StateHandle<number>;
  itemNumHandle: StateHandle<number>;
  duplicateFindings: LensHit[];
  fetchMore: (scrollToHit: boolean) => unknown;
  endComponent?: React.ReactNode;
  overrideHitState: StateHandle<LensHit | undefined>;
  setPageNum: (pageNum: number) => void;
  onHitIgnored: (hit: LensHit) => void;
  showTitle?: boolean;
}

const HitCardVirtuoso = (props: HitCardVirtuosoProps) => {
  const {
    lensId,
    caseId,
    intake,
    items,
    itemsFullCount,
    loading,
    fetchMore,
    numShowingHandle,
    firstShowingHandle,
    itemNumHandle,
    setPageNum,
    showTitle,
    onHitIgnored,
  } = props;

  const [numShowing, setNumShowing] = numShowingHandle;
  const [firstShowing, setFirstShowing] = firstShowingHandle;
  const [itemNum, setItemNum] = itemNumHandle;
  const cardCollapsedState = React.useState(true);
  const ref = React.useRef<VirtuosoHandle>();

  const updateItemNum = (num: number, item: LensHit | MatchingBlock) => {
    setItemNum(num);
    setPageNum(item.block.originalPageNumber);
    if (!cardCollapsedState[0]) {
      if (ref.current) {
        setTimeout(() => {
          ref.current?.scrollToIndex({
            index: num,
          });
        }, 500);
      }
    }
    cardCollapsedState[1](true);
  };

  return (
    <Virtuoso
      style={{
        paddingRight: "1rem",
        marginRight: "1rem",
        height: "100%",
        width: "100%",
      }}
      totalCount={items.length}
      ref={(handle) => {
        if (handle) ref.current = handle;
      }}
      overscan={100}
      itemsRendered={(items) => {
        const firstItem = items[0];
        if (firstItem !== undefined) {
          setFirstShowing(firstItem.index);
        }
        setNumShowing(items.length);
      }}
      initialTopMostItemIndex={itemNum}
      endReached={() => {
        fetchMore(false);
      }}
      components={{
        Footer: () => (
          <Flex m={1} justifyContent="center" alignItems="center">
            {loading ? (
              <>
                <Typography>
                  <b>Loading</b>
                </Typography>
                <LoadingSpinner size={50} />
              </>
            ) : itemsFullCount === 0 ? (
              <Typography>
                <b>No results</b>
              </Typography>
            ) : itemsFullCount - (firstShowing + numShowing) <= 3 ? (
              props.endComponent
            ) : (
              <div />
            )}
          </Flex>
        ),
      }}
      itemContent={(idx) => {
        const item = items[idx];

        if (!item) {
          return <Skeleton />;
        }

        const { entityOfItem, hitInForm, hit } = processCardItem(item);
        const isNegated = entityOfItem?.isNegated;
        const card =
          idx === itemNum ? (
            <HitCard
              selected
              title={showTitle && hit ? `${hit.ruleSubtype} - ${hit.ruleDescription}` : ``}
              showSurroundings
              lensId={lensId}
              caseId={caseId}
              intake={intake}
              item={item}
              childHits={props.duplicateFindings}
              setPageNum={setPageNum}
              selectedChildHitState={props.overrideHitState}
              idx={idx}
              hitInForm={hitInForm}
              isNegated={isNegated}
              itemsFullCount={itemsFullCount}
              onHitIgnored={onHitIgnored}
            />
          ) : (
            <HitCard
              title={showTitle && hit ? `${hit.ruleSubtype} - ${hit.ruleDescription}` : ``}
              selected={false}
              showSurroundings={true}
              childHits={[]}
              lensId={lensId}
              caseId={caseId}
              intake={intake}
              item={item}
              onClick={() => updateItemNum(idx, item)}
              selectedChildHitState={props.overrideHitState}
              idx={idx}
              hitInForm={hitInForm}
              isNegated={isNegated}
              itemsFullCount={itemsFullCount}
              onHitIgnored={onHitIgnored}
            />
          );

        return <HitCardBox flexDirection="row">{card}</HitCardBox>;
      }}
    />
  );
};

export default HitCardVirtuoso;
